import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Stack,
  Checkbox,
  Alert,
} from '@mui/joy';
import {
  standardMaterialsApi,
  materialSpecsApi,
  type StandardMaterial,
  type MaterialSpecification,
} from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  open: boolean;
  onClose: () => void;
  material: StandardMaterial | null;
  onSave: () => void;
}

const emptyForm = {
  name: '',
  material_spec: '',
  calculation_key: '',
  default_quantity_formula: '',
  is_active: true,
};

export default function StandardMaterialForm({ open, onClose, material, onSave }: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [specs, setSpecs] = useState<MaterialSpecification[]>([]);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (open) {
      loadSpecs();
      if (material) {
        setFormData({
          name: material.name,
          material_spec: material.material_spec != null ? String(material.material_spec) : '',
          calculation_key: material.calculation_key,
          default_quantity_formula: material.default_quantity_formula || '',
          is_active: material.is_active,
        });
      } else {
        setFormData(emptyForm);
      }
      setError(null);
    }
  }, [open, material]);

  const loadSpecs = async () => {
    try {
      const response = await materialSpecsApi.list({ is_active: true, page: 1 });
      setSpecs(response?.results || []);
    } catch {
      setError('Failed to load material specifications');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.calculation_key.trim()) {
      setError('Calculation key is required');
      return;
    }

    setLoading(true);
    try {
      const payload: Partial<StandardMaterial> = {
        name: formData.name.trim(),
        material_spec: formData.material_spec ? parseInt(formData.material_spec, 10) : null,
        calculation_key: formData.calculation_key.trim(),
        default_quantity_formula: formData.default_quantity_formula,
        is_active: formData.is_active,
      };

      if (material) {
        await standardMaterialsApi.update(material.id, payload);
        showSuccess('Standard material updated successfully');
      } else {
        await standardMaterialsApi.create(payload);
        showSuccess('Standard material created successfully');
      }
      onSave();
      onClose();
    } catch (err: any) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.calculation_key?.[0] ||
        err.response?.data?.name?.[0] ||
        err.response?.data?.non_field_errors?.[0] ||
        'Failed to save standard material';
      setError(detail);
      showError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '90%', maxWidth: 500 }}>
        <ModalClose />
        <Typography level="h3" sx={{ mb: 2 }}>
          {material ? 'Edit Standard Material' : 'Add Standard Material'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && <Alert color="danger">{error}</Alert>}

            <FormControl required>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Standard material name"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Calculation Key</FormLabel>
              <Input
                value={formData.calculation_key}
                onChange={(e) => setFormData({ ...formData, calculation_key: e.target.value })}
                placeholder="Unique calculation engine key"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Material Specification</FormLabel>
              <Select
                value={formData.material_spec}
                onChange={(_, value) => setFormData({ ...formData, material_spec: (value as string) ?? '' })}
                placeholder="Optional — link to a specification"
              >
                <Option value="">None</Option>
                {specs.map((s) => (
                  <Option key={s.id} value={String(s.id)}>
                    {s.category_name ? `${s.category_name} — ${s.name}` : s.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Default Quantity Formula</FormLabel>
              <Textarea
                minRows={2}
                value={formData.default_quantity_formula}
                onChange={(e) => setFormData({ ...formData, default_quantity_formula: e.target.value })}
                placeholder="e.g., area / 100 * 1.1"
              />
            </FormControl>

            <FormControl>
              <Checkbox
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </FormControl>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button type="submit" color="primary" loading={loading} sx={{ flex: 1 }}>
                {material ? 'Update' : 'Create'}
              </Button>
              <Button variant="outlined" onClick={onClose} disabled={loading} sx={{ flex: 1 }}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
